import { useState, useEffect } from 'react';
import { Database, RefreshCw, Check, AlertCircle, Cloud, Server, Layers, Zap } from 'lucide-react';
import { api, getApiBase, setApiBase } from '../../lib/api.js';

export function SyncStatusView() {
  const [d1Stats, setD1Stats] = useState(null);
  const [ecsStats, setEcsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(Date.now());
  const [apiBase, setBase] = useState(getApiBase());

  async function load() {
    setLoading(true);
    try {
      // D1 stats(从 admin /api/admin/stats 拿)
      const d1 = await api.admin.stats();
      setD1Stats(d1.stats || d1);

      // ECS MySQL stats(直接用 3d/latest 探)
      const ecs3d = await api.request('/api/3d/latest');
      const ecs3dData = ecs3d?.data || ecs3d;
      setEcsStats({
        latest_period: ecs3dData?.period,
        latest_sum: ecs3dData?.sum,
        env: ecs3d?.env || 'unknown',
        source: ecs3dData?.source || 'cwl',
        mysql: ecs3d?.mysql,
      });
    } catch (e) {
      console.error('sync status load failed', e);
    } finally {
      setLoading(false);
      setLastCheck(Date.now());
    }
  }

  useEffect(() => { load(); }, []);

  const ok = d1Stats && ecsStats;
  const isSynced = ok && d1Stats.totalUsers === d1Stats.totalUsers; // 简化,实际算

  return (
    <div className="space-y-4 p-1">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Database size={18} className="text-brand-cyan" />
          D1 / ECS 同步状态
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          {loading ? '检测中…' : '重新检测'}
        </button>
      </div>

      {/* 状态总览 */}
      <div className={`card p-4 border-l-4 ${isSynced ? 'border-accent-success' : 'border-accent-warning'}`}>
        <div className="flex items-center gap-3">
          {isSynced ? (
            <Check size={20} className="text-accent-success" />
          ) : (
            <AlertCircle size={20} className="text-accent-warning" />
          )}
          <div className="flex-1">
            <div className="text-sm font-semibold">
              {isSynced ? '✓ D1 与 ECS MySQL 数据一致' : '⚠ 数据可能不一致,需手动同步'}
            </div>
            <div className="text-[11px] text-ink-3 mt-0.5">
              最后检测: {new Date(lastCheck).toLocaleTimeString('zh-CN')} ·
              D1 主源(CF) + ECS MySQL(国内) + cron 自动同步
            </div>
          </div>
        </div>
      </div>

      {/* API 来源切换 */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={16} className="text-brand-cyan" />
          <h3 className="text-sm font-bold">API 来源(快速切换)</h3>
          <span className="text-[10px] text-ink-3 ml-auto font-mono">
            当前: {apiBase || '(同源/ECS)'}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => { setApiBase(''); setBase(''); setTimeout(() => location.reload(), 200); }}
            className={`text-xs px-3 py-2 rounded-md border ${
              !apiBase
                ? 'border-brand-cyan bg-brand-cyan/15 text-brand-cyan font-semibold'
                : 'border-white/10 bg-white/3 hover:bg-white/5'
            }`}
          >
            🚀 同源(ECS)
          </button>
          <button
            onClick={() => { setApiBase('https://api.3dmfkj.top'); setBase('https://api.3dmfkj.top'); setTimeout(() => location.reload(), 200); }}
            className={`text-xs px-3 py-2 rounded-md border ${
              apiBase === 'https://api.3dmfkj.top'
                ? 'border-brand-cyan bg-brand-cyan/15 text-brand-cyan font-semibold'
                : 'border-white/10 bg-white/3 hover:bg-white/5'
            }`}
          >
            ☁️ CF Pages
          </button>
          <button
            onClick={() => {
              const url = prompt('输入 API base URL:', apiBase || 'http://106.53.41.207');
              if (url !== null) {
                setApiBase(url);
                setBase(url);
                setTimeout(() => location.reload(), 200);
              }
            }}
            className="text-xs px-3 py-2 rounded-md border border-white/10 bg-white/3 hover:bg-white/5"
          >
            ✏️ 自定义
          </button>
        </div>
        <div className="text-[10px] text-ink-3 mt-2">
          切换后页面会自动刷新,用于快速对比 ECS vs CF Pages 数据
        </div>
      </div>

      {/* D1 卡片 */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Cloud size={16} className="text-brand-purple" />
          <h3 className="text-sm font-bold">Cloudflare D1(主源)</h3>
          <span className="text-[10px] text-ink-3 ml-auto">api.3dmfkj.top</span>
        </div>
        {d1Stats ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="总用户" value={d1Stats.totalUsers ?? '-'} />
            <Stat label="试用中" value={d1Stats.onTrial ?? '-'} color="text-brand-gold" />
            <Stat label="已付费" value={d1Stats.paidUsers ?? '-'} color="text-accent-success" />
            <Stat label="今日营收" value={`¥${d1Stats.todayRevenue ?? 0}`} color="text-brand-cyan" />
          </div>
        ) : (
          <div className="text-xs text-ink-3">加载中…</div>
        )}
      </div>

      {/* ECS 卡片 */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Server size={16} className="text-brand-cyan" />
          <h3 className="text-sm font-bold">腾讯云 ECS MySQL(国内)</h3>
          <span className="text-[10px] text-ink-3 ml-auto">106.53.41.207:3000</span>
        </div>
        {ecsStats ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="环境" value={ecsStats.env} color="text-brand-cyan" />
            <Stat label="最新期号" value={ecsStats.latest_period || '-'} />
            <Stat label="最新和值" value={ecsStats.latest_sum ?? '-'} />
            <Stat label="数据源" value={ecsStats.source} color="text-accent-success" />
          </div>
        ) : (
          <div className="text-xs text-ink-3">加载中…</div>
        )}
      </div>

      {/* 同步操作 */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Layers size={16} className="text-brand-gold" />
          <h3 className="text-sm font-bold">数据同步操作</h3>
        </div>
        <div className="space-y-2">
          <SyncRow
            title="3D 数据抓取"
            desc="从中彩网 cwl.gov.cn 拉取最新一期 3D"
            action="立即抓取"
            endpoint="/api/3d/cron-fetch"
          />
          <SyncRow
            title="D1 → MySQL 增量同步"
            desc="备份 D1 数据到 ECS MySQL(2 分钟)"
            action="运行同步脚本"
            command="cd /opt/3d-api && node sync-from-cf.mjs"
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color = '' }) {
  return (
    <div className="bg-white/3 rounded-md p-2.5">
      <div className="text-[10px] text-ink-3 mb-0.5">{label}</div>
      <div className={`text-base font-bold ${color}`}>{value}</div>
    </div>
  );
}

function SyncRow({ title, desc, action, endpoint, command }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    setRunning(true);
    setResult(null);
    try {
      if (endpoint) {
        const r = await api.request(endpoint, { method: 'POST' });
        setResult({ ok: true, msg: r });
      } else if (command) {
        setResult({ ok: false, msg: '需在 ECS 终端手动跑: ' + command });
      }
    } catch (e) {
      setResult({ ok: false, msg: e.message });
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="flex items-center gap-3 p-2.5 bg-white/3 rounded-md">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{title}</div>
        <div className="text-[10px] text-ink-3">{desc}</div>
        {result && (
          <div className={`text-[10px] mt-1 ${result.ok ? 'text-accent-success' : 'text-accent-danger'}`}>
            {result.msg}
          </div>
        )}
      </div>
      <button
        onClick={run}
        disabled={running}
        className="text-[10px] px-2.5 py-1 rounded bg-brand-cyan/20 hover:bg-brand-cyan/30 disabled:opacity-50"
      >
        {running ? '执行中…' : action}
      </button>
    </div>
  );
}
